import React from 'react';
import {Button, Stack, TableCell, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import dayjs from 'dayjs';
import {useRecordContext} from 'components/record/useRecordContext';
import {OpenInNew} from '@mui/icons-material';
import EventStatusBadge from 'components/admin/EventStatusBadge';

const formatCategory = (value?: string) => {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatOwners = (users?: {name?: string}[]) => {
  if (!users?.length) return '—';
  return users.map((user) => user.name?.trim()).filter(Boolean).join(', ') || '—';
};

const formatDateRange = (startDate?: string, endDate?: string) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (start.isSame(end, 'day')) {
    return `${start.format('MMM D, HH:mm')} → ${end.format('HH:mm')}`;
  }
  return `${start.format('MMM D')}–${end.format('D')}`;
};

const hideOnMobileSx = {display: {xs: 'none', md: 'table-cell'}};

type ListColumnProps = {label?: string; source?: string; width?: string; hideOnMobile?: boolean; defaultHidden?: boolean; hideable?: boolean};

export const EventNameField = (_props: ListColumnProps) => {
  const {record} = useRecordContext();
  const count = record.countSubEvents || 0;
  return (
    <TableCell sx={{pr: {xs: 0.5, md: 2}, verticalAlign: {xs: 'top', md: 'middle'}}}>
      <Stack spacing={0.5}>
        <Typography
          component={Link}
          to={`/my_events/${record.slug}`}
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            textDecoration: 'none',
            wordBreak: 'break-word',
            '&:hover': {color: 'primary.main'}
          }}
        >
          {record.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{display: {xs: 'none', md: 'block'}}}>
          {count} {count === 1 ? 'subevent' : 'subevents'}
        </Typography>
        <Stack spacing={0.75} sx={{display: {xs: 'flex', md: 'none'}}}>
          <Typography variant="caption" color="text.secondary">
            {formatDateRange(record.startDate, record.endDate)} · {formatOwners(record.users)} · {formatCategory(record.category)} · {count}{' '}
            {count === 1 ? 'subevent' : 'subevents'}
          </Typography>
          <EventStatusBadge status={record.status} hidden={record.hidden} />
          {record.status === 'live' && (
            <Button
              size="small"
              variant="contained"
              component="a"
              href={`${import.meta.env.VITE_DOMAIN_EVENTS}${record.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNew fontSize="small" />}
              sx={{alignSelf: 'flex-start'}}
            >
              Open Live
            </Button>
          )}
        </Stack>
      </Stack>
    </TableCell>
  );
};

export const EventDateRangeField = (_props: ListColumnProps) => {
  const {record} = useRecordContext();
  const start = dayjs(record.startDate);
  const end = dayjs(record.endDate);
  const sameDay = start.isSame(end, 'day');

  return (
    <TableCell sx={hideOnMobileSx}>
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={600}>
          {sameDay ? start.format('MMM D, HH:mm') : `${start.format('MMM D')}–${end.format('D')}`}
        </Typography>
        {sameDay && (
          <Typography variant="caption" color="text.secondary">
            → {end.format('HH:mm')}
          </Typography>
        )}
      </Stack>
    </TableCell>
  );
};

export const EventCategoryField = (_props: ListColumnProps) => {
  const {record} = useRecordContext();
  return (
    <TableCell sx={hideOnMobileSx}>
      <Typography variant="body2">{formatCategory(record.category)}</Typography>
    </TableCell>
  );
};

export const EventOwnerField = (_props: ListColumnProps) => {
  const {record} = useRecordContext();
  return (
    <TableCell sx={hideOnMobileSx}>
      <Typography variant="body2">{formatOwners(record.users)}</Typography>
    </TableCell>
  );
};

export const EventLiveActionField = (_props: ListColumnProps) => {
  const {record} = useRecordContext();
  if (record.status !== 'live') {
    return <TableCell sx={hideOnMobileSx} />;
  }
  return (
    <TableCell sx={hideOnMobileSx}>
      <Button
        size="small"
        variant="contained"
        component="a"
        href={`${import.meta.env.VITE_DOMAIN_EVENTS}${record.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<OpenInNew fontSize="small" />}
      >
        Open Live
      </Button>
    </TableCell>
  );
};
